import React, { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Typography,
} from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { EntradaProdutoService } from "../service/entrada_produto_service";

const VAZIO = "(vazio)";

const OPERACOES = {
	EDICAO: { label: "Edição", color: "#1a9e8f" },
	CLASSIFICACAO: { label: "Classificação de qualidade", color: "#1976d2" },
	EXCLUSAO: { label: "Exclusão", color: "#f44336" },
};

function formatarDataHora(valor) {
	if (!valor) return "-";

	const data = new Date(valor);
	if (Number.isNaN(data.getTime())) return "-";

	return data.toLocaleString("pt-BR", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/**
 * Trilha de alterações de uma entrada de produto: um bloco por operação, do
 * mais recente para o mais antigo, com o antes/depois de cada campo alterado.
 */
function ModalHistoricoEntrada({ open, onClose, entrada }) {
	const [loading, setLoading] = useState(false);
	const [erro, setErro] = useState("");
	const [historico, setHistorico] = useState([]);

	const entradaId = entrada?.id;

	const carregar = useCallback(async () => {
		setLoading(true);
		setErro("");
		try {
			const entradaProdutoService = new EntradaProdutoService();
			const registros = await entradaProdutoService.getHistorico(entradaId);
			setHistorico(Array.isArray(registros) ? registros : []);
		} catch (error) {
			console.error("Erro ao carregar histórico:", error);
			setErro(error.message || "Erro ao carregar histórico");
		} finally {
			setLoading(false);
		}
	}, [entradaId]);

	useEffect(() => {
		if (open && entradaId) {
			carregar();
			return;
		}

		setHistorico([]);
		setErro("");
	}, [open, entradaId, carregar]);

	const nomeProduto = entrada?.produtoUnidadeDistribuidora?.cultivo
		? `${entrada.produtoUnidadeDistribuidora.cultivo.cultura || ""}${
				entrada.produtoUnidadeDistribuidora.cultivo.variedade
					? ` - ${entrada.produtoUnidadeDistribuidora.cultivo.variedade}`
					: ""
		  }`
		: "";

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ bgcolor: "#1a9e8f", color: "white", fontSize: "18px", fontWeight: 600 }}>
				Histórico de Alterações
			</DialogTitle>
			<DialogContent sx={{ mt: 3, px: 3 }}>
				{(nomeProduto || entrada?.fornecedor?.nome) && (
					<Box sx={{ mb: 3, p: 2, bgcolor: "#f9f9f9", borderRadius: 1 }}>
						{nomeProduto && (
							<Typography variant="body1" sx={{ fontSize: "15px" }}>
								<strong>Produto:</strong> {nomeProduto}
							</Typography>
						)}
						{entrada?.fornecedor?.nome && (
							<Typography variant="body1" sx={{ fontSize: "15px" }}>
								<strong>Fornecedor:</strong> {entrada.fornecedor.nome}
							</Typography>
						)}
					</Box>
				)}

				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
						<CircularProgress sx={{ color: "#1a9e8f" }} />
					</Box>
				) : erro ? (
					<Alert severity="error">{erro}</Alert>
				) : historico.length === 0 ? (
					<Typography sx={{ py: 4, textAlign: "center", color: "#999" }}>
						Esta entrada ainda não sofreu alterações.
					</Typography>
				) : (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						{historico.map((registro) => {
							const operacao = OPERACOES[registro.operacao] || {
								label: registro.operacao,
								color: "#666",
							};
							const alteracoes = Array.isArray(registro.alteracoes) ? registro.alteracoes : [];

							return (
								<Box
									key={registro.id}
									sx={{
										border: "1px solid #e0e0e0",
										borderRadius: "8px",
										overflow: "hidden",
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											gap: 1,
											flexWrap: "wrap",
											px: 2,
											py: 1.5,
											bgcolor: "#fafafa",
										}}
									>
										<Chip
											label={operacao.label}
											size="small"
											sx={{
												bgcolor: operacao.color,
												color: "white",
												fontWeight: 600,
											}}
										/>
										<Typography variant="body2" sx={{ color: "#666" }}>
											{formatarDataHora(registro.createdAt)}
											{registro.responsavelNome ? ` · ${registro.responsavelNome}` : ""}
										</Typography>
									</Box>

									<Divider />

									<Box sx={{ px: 2, py: 1.5 }}>
										{alteracoes.length === 0 ? (
											<Typography variant="body2" sx={{ color: "#999" }}>
												Sem detalhes registrados.
											</Typography>
										) : (
											alteracoes.map((alteracao, idx) => (
												<Box
													key={`${registro.id}-${alteracao.campo}-${idx}`}
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
														flexWrap: "wrap",
														py: 0.5,
													}}
												>
													<Typography
														variant="body2"
														sx={{ color: "#666", fontWeight: 600, minWidth: 170 }}
													>
														{alteracao.rotulo}
													</Typography>
													<Typography
														variant="body2"
														sx={{ color: "#999", textDecoration: "line-through" }}
													>
														{alteracao.de ?? VAZIO}
													</Typography>
													<ArrowRightAltIcon sx={{ color: "#999", fontSize: 18 }} />
													<Typography variant="body2" sx={{ color: "#333", fontWeight: 500 }}>
														{alteracao.para ?? VAZIO}
													</Typography>
												</Box>
											))
										)}
									</Box>
								</Box>
							);
						})}
					</Box>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button
					onClick={onClose}
					variant="contained"
					sx={{
						bgcolor: "#1a9e8f",
						"&:hover": { bgcolor: "#138a7d" },
						textTransform: "none",
						px: 3,
					}}
				>
					Fechar
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default ModalHistoricoEntrada;
